import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Heart, ArrowRight, X, CheckCircle2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function RenewPledge() {
  const [params] = useSearchParams();
  const token = params.get("token");
  const [pledge, setPledge] = useState(null);
  const [status, setStatus] = useState("loading");
  const [loading, setLoading] = useState(false);
  const [showCancel, setShowCancel] = useState(false);
  const [cancelled, setCancelled] = useState(false);

  useEffect(() => {
    if (!token) { setStatus("not_found"); return; }
    (async () => {
      try {
        const r = await axios.get(`${API}/donations/pledge/${token}`);
        setPledge(r.data);
        setStatus("ok");
        if (window.location.hash === "#cancelar") setShowCancel(true);
      } catch (e) {
        setStatus("not_found");
      }
    })();
  }, [token]);

  const renew = async () => {
    setLoading(true);
    try {
      const r = await axios.post(`${API}/donations/pledge/${token}/checkout`, {
        origin_url: window.location.origin,
      });
      if (r.data.url) window.location.href = r.data.url;
    } catch (e) {
      toast.error("Erro ao iniciar pagamento.");
      setLoading(false);
    }
  };

  const cancel = async () => {
    if (!pledge) return;
    setLoading(true);
    try {
      // Cancel via admin endpoint is gated; use member's own link. The cancel
      // token isn't exposed in the public GET, so we re-derive via the URL hash
      // pattern: the email link the user receives passes ?token=<id>#cancelar.
      // For safety, the backend public cancel endpoint requires the cancel_token
      // — but the email always provides it. Users coming from the renew link
      // without that hash will trigger this fallback that re-asks confirmation
      // and uses the pledge id only (best-effort cancel via admin support).
      // → We implement a simple "request cancellation" flow via email instead.
      await axios.post(`${API}/contact`, {
        full_name: pledge.full_name,
        email: pledge.email,
        subject: "Cancelar donativo recorrente",
        message: `Peço o cancelamento do meu donativo recorrente (id: ${token}). Obrigado.`,
        language: pledge.language || "pt",
      });
      setCancelled(true);
    } catch (e) {
      toast.error("Erro a enviar pedido de cancelamento.");
    } finally {
      setLoading(false);
    }
  };

  if (status === "loading") {
    return <div className="container-page pt-32 pb-32"><p className="text-[hsl(var(--muted-foreground))]">A carregar…</p></div>;
  }
  if (status === "not_found") {
    return (
      <div className="container-page pt-32 pb-32" data-testid="renew-not-found">
        <div className="overline">Donativo recorrente</div>
        <h1 className="display-heading text-4xl sm:text-5xl mt-3">Link inválido ou expirado.</h1>
        <p className="mt-6 text-[hsl(var(--muted-foreground))]">
          Este donativo recorrente já não está ativo ou o link expirou.
        </p>
        <Link to="/apoiar" className="mt-8 inline-flex items-center gap-2 text-sm uppercase tracking-[0.2em] text-[hsl(var(--primary))] font-medium" data-testid="renew-back-to-support">
          Voltar a Apoiar <ArrowRight className="h-4 w-4" />
        </Link>
      </div>
    );
  }

  if (cancelled) {
    return (
      <div className="container-narrow pt-32 pb-32" data-testid="renew-cancelled">
        <CheckCircle2 className="h-12 w-12 text-[hsl(var(--primary))]" />
        <h1 className="display-heading text-4xl mt-5">Pedido de cancelamento enviado.</h1>
        <p className="mt-5 text-[hsl(var(--muted-foreground))] leading-relaxed">
          Recebemos o seu pedido. A equipa CIEES Portugal irá processar o cancelamento e enviar-lhe-á uma confirmação por email nas próximas 24h. Obrigado pelo seu apoio.
        </p>
      </div>
    );
  }

  return (
    <div data-testid="renew-pledge-page">
      <section className="bg-[hsl(var(--primary))] text-white py-20 relative overflow-hidden">
        <div className="absolute inset-0 grain pointer-events-none" />
        <div className="container-narrow relative text-center">
          <Heart className="h-12 w-12 text-[hsl(var(--accent))] mx-auto" />
          <div className="overline text-white/80 mt-6">Donativo Recorrente</div>
          <h1 className="display-heading text-5xl sm:text-6xl mt-5 leading-[1.05]">Renovar o seu apoio.</h1>
          <p className="mt-8 text-lg text-white/85 leading-relaxed">
            Olá {pledge.full_name}, chegou o momento de concluir o donativo mensal de <strong className="text-[hsl(var(--accent))]">{Number(pledge.amount_eur).toFixed(2)} €</strong>.
          </p>
        </div>
      </section>

      <section className="container-narrow py-16">
        <div className="bg-white border border-[hsl(var(--border))] p-8 sm:p-10">
          <div className="overline">Detalhes</div>
          <div className="mt-5 grid sm:grid-cols-2 gap-x-12 gap-y-3 text-sm">
            <Row label="Nome">{pledge.full_name}</Row>
            <Row label="Email">{pledge.email}</Row>
            <Row label="Valor mensal">{Number(pledge.amount_eur).toFixed(2)} €</Row>
            <Row label="Frequência">Mensal</Row>
            {pledge.company && <Row label="Empresa">{pledge.company}</Row>}
            {pledge.last_charged_at && (
              <Row label="Último donativo">{new Date(pledge.last_charged_at).toLocaleDateString("pt-PT")}</Row>
            )}
          </div>

          <button
            onClick={renew}
            disabled={loading}
            className="mt-8 w-full inline-flex items-center justify-center gap-2 px-6 py-4 bg-[hsl(var(--accent))] text-[#1A201F] text-sm uppercase tracking-[0.22em] font-semibold hover:bg-[#B3922E] transition-colors disabled:opacity-60"
            data-testid="renew-pay-button"
          >
            {loading ? "A processar…" : `Pagar ${Number(pledge.amount_eur).toFixed(2)} € agora`} <ArrowRight className="h-4 w-4" />
          </button>
          <p className="mt-3 text-xs text-center text-[hsl(var(--muted-foreground))]">Pagamento seguro via Stripe</p>

          <div className="mt-10 pt-6 border-t border-[hsl(var(--border))]">
            {!showCancel ? (
              <button
                onClick={() => setShowCancel(true)}
                className="text-xs text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--primary))] underline"
                data-testid="renew-show-cancel"
              >
                Cancelar este donativo recorrente
              </button>
            ) : (
              <div className="bg-[hsl(var(--secondary))] p-5" data-testid="renew-cancel-confirm">
                <div className="flex items-start justify-between gap-3">
                  <p className="text-sm text-[hsl(var(--foreground))]">Tem a certeza? Enviaremos o seu pedido à nossa equipa e o seu donativo recorrente será desativado em até 24h.</p>
                  <button onClick={() => setShowCancel(false)} className="text-[hsl(var(--muted-foreground))]"><X className="h-4 w-4" /></button>
                </div>
                <button
                  onClick={cancel}
                  disabled={loading}
                  className="mt-4 px-5 py-2 bg-[hsl(var(--primary))] text-white text-xs uppercase tracking-[0.2em] font-medium hover:bg-[hsl(var(--primary))]/90 disabled:opacity-60"
                  data-testid="renew-cancel-confirm-button"
                >
                  {loading ? "A enviar…" : "Confirmar cancelamento"}
                </button>
              </div>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}

function Row({ label, children }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-[0.18em] text-[hsl(var(--muted-foreground))]">{label}</div>
      <div className="mt-1 text-[hsl(var(--foreground))]">{children}</div>
    </div>
  );
}
